import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, RotateCcw, Check, Play, Film, Utensils, LineChart, Dumbbell, Flag } from "lucide-react";
import VitalityIntake from "@/components/vitality/Intake";
import FitnessHero from "@/components/vitality/FitnessHero";
import { buildProgram, macroTargets, PLATES, SECONDARY, MOVEMENTS } from "@/lib/vitalityData";
import useProgress from "@/lib/useProgress";
import { PLATFORMS } from "@/lib/platforms";

const FOREST = "#0F3324";
const GREEN = "#4CAF7D";
const DEEP = "#1E6B45";
const LIME = "#A8E06A";
const INK = "#16281D";
const PAPER = "#EEF6EF";

const GOAL_LABEL = { fat: "Less of me", muscle: "More muscle", strong: "Genuinely strong", wind: "Better wind", painfree: "Pain free", habit: "Become the person" };
const TIER_LABEL = ["Floor only", "Dumbbells and bands", "Full gym"];
const LIFT = [86, 80, 74, 66, 60, 53, 45, 38, 31, 26, 18, 9];
const PATH = "M 24 118 C 200 118, 300 88, 430 74 C 560 60, 640 40, 800 28 C 950 18, 1080 12, 1156 8";

function Arc({ program, log, open, setOpen }) {
  const done = program.weeks.filter((w) => log[w.n]?.done).length;
  return (
    <div className="overflow-x-auto lg:overflow-visible pb-4 -mx-5 px-5 sm:-mx-8 sm:px-8" data-testid="vitality-arc">
      <div className="relative arc-inner">
        <svg viewBox="0 0 1180 140" className="w-full h-[140px]" preserveAspectRatio="none">
          <defs>
            <linearGradient id="ridge" x1="0" x2="1">
              <stop offset="0%" stopColor={LIME} /><stop offset="60%" stopColor={GREEN} /><stop offset="100%" stopColor={DEEP} />
            </linearGradient>
          </defs>
          {[1, 2, 3].map((p) => (
            <line key={p} x1={24 + p * 283} y1={10} x2={24 + p * 283} y2={130} stroke="rgba(30,107,69,0.12)" strokeWidth="1" strokeDasharray="3 5" />
          ))}
          <path d={PATH} fill="none" stroke="rgba(30,107,69,0.18)" strokeWidth="12" strokeLinecap="round" />
          <path d={PATH} fill="none" stroke="rgba(30,107,69,0.25)" strokeWidth="2" strokeLinecap="round" strokeDasharray="3 6" />
          <motion.path d={PATH} fill="none" stroke="url(#ridge)" strokeWidth="4" strokeLinecap="round" pathLength={1}
            initial={{ strokeDashoffset: 1 }} animate={{ strokeDashoffset: 1 - done / 12 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            style={{ strokeDasharray: 1 }} />
        </svg>
        <div className="grid grid-cols-12 gap-2 -mt-[126px]">
          {program.weeks.map((w, i) => {
            const isDone = !!log[w.n]?.done;
            const active = open === w.n;
            return (
              <button key={w.n} onClick={() => setOpen(active ? null : w.n)} className="relative flex flex-col items-center group" style={{ paddingTop: LIFT[i] }} data-testid={`week-node-${w.n}`}>
                {w.n % 3 === 0 && <Flag size={11} className="absolute" style={{ top: LIFT[i] - 18, color: DEEP, opacity: 0.5 }} />}
                <span className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold transition-transform group-hover:scale-110"
                  style={isDone ? { background: DEEP, color: "#fff", boxShadow: "0 8px 22px -8px rgba(30,107,69,0.9)" }
                    : { background: "#fff", border: `2px solid ${active ? DEEP : "rgba(30,107,69,0.3)"}`, color: DEEP }}>
                  {isDone ? <Check size={14} /> : w.n}
                </span>
                <span className="text-[8px] tracking-[0.14em] uppercase mt-2 whitespace-nowrap" style={{ color: "rgba(22,40,29,0.45)" }}>{w.deload ? "deload" : w.phase.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekCard({ w, program, l, onToggle }) {
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="rounded-[1.8rem] overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.1)", boxShadow: "0 26px 60px -34px rgba(15,51,36,0.5)" }} data-testid={`week-detail-${w.n}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-7 p-7 sm:p-9">
          <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>Week {w.n} · phase {w.phase.n}, {w.phase.name}</p>
          <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight" style={{ color: INK }}>{w.deload ? "The week you go easy on purpose" : w.phase.name}</h3>
          <p className="mt-3 text-[15px]" style={{ color: "rgba(22,40,29,0.65)" }}>{w.focus}</p>
          <div className="mt-5 grid grid-cols-3 gap-2">
            {[["Sets", w.sets], ["Reps", w.reps], ["Effort", w.rpe]].map(([k, v]) => (
              <div key={k} className="rounded-2xl px-4 py-3" style={{ background: "rgba(76,175,125,0.1)" }}>
                <p className="text-[9px] tracking-[0.16em] uppercase" style={{ color: DEEP, opacity: 0.7 }}>{k}</p>
                <p className="text-sm mt-0.5" style={{ color: INK }}>{v}</p>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>This week's sessions</p>
            <div className="mt-2 space-y-2.5">
              {program.sessions.map((s, i) => (
                <div key={s.key} className="rounded-[1.4rem] overflow-hidden" style={{ border: "1px solid rgba(22,40,29,0.1)" }} data-testid={`week-session-${w.n}-${i}`}>
                  <div className="flex items-baseline justify-between gap-3 px-5 py-3.5" style={{ background: "rgba(76,175,125,0.09)" }}>
                    <p className="font-display text-lg" style={{ color: INK }}>{s.name}</p>
                    <p className="text-[11px]" style={{ color: DEEP }}>{s.minutes} min · {w.sets} · {w.reps}</p>
                  </div>
                  <div className="divide-y" style={{ borderColor: "rgba(22,40,29,0.07)" }}>
                    {s.blocks.map((m, n) => (
                      <div key={m.id} className="px-5 py-3.5 group transition-colors hover:bg-[rgba(76,175,125,0.05)]" data-testid={`exercise-${m.id}`}>
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-semibold shrink-0" style={{ background: "rgba(30,107,69,0.1)", color: DEEP }}>{n + 1}</span>
                          <p className="font-display text-[16.5px] flex-1 min-w-0" style={{ color: INK }}>{m.name}</p>
                          <span className="text-[10.5px] tabular-nums shrink-0" style={{ color: DEEP }}>{w.sets} × {w.reps}</span>
                          <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[9.5px] tracking-[0.1em] uppercase shrink-0"
                            style={{ background: "rgba(22,40,29,0.06)", color: "rgba(22,40,29,0.5)" }}>
                            <Film size={9} /> video soon
                          </span>
                        </div>
                        {m.cues && (
                          <p className="text-[12.5px] mt-2 pl-9 leading-snug" style={{ color: "rgba(22,40,29,0.62)" }}>{m.cues}</p>
                        )}
                        {m.mistake && (
                          <p className="text-[12px] mt-1.5 pl-9 leading-snug" style={{ color: "#A8452F" }}>
                            <span className="font-semibold">Most common miss: </span>{m.mistake}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-2.5 pl-9">
                          {m.easier && <span className="rounded-full px-2.5 py-1 text-[10.5px]" style={{ background: "rgba(76,175,125,0.12)", color: DEEP }}>easier · {m.easier}</span>}
                          {m.harder && <span className="rounded-full px-2.5 py-1 text-[10.5px]" style={{ background: "rgba(168,224,106,0.22)", color: DEEP }}>harder · {m.harder}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 rounded-2xl px-4 py-3" style={{ background: "rgba(168,224,106,0.18)" }}>
            <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: DEEP }}>Food this block</p>
            <p className="text-sm mt-1" style={{ color: INK }}>{w.nutrition}</p>
          </div>
          {w.marker && (
            <div className="mt-4 border-l-2 pl-4" style={{ borderColor: GREEN }}>
              <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>What you should notice</p>
              <p className="text-sm mt-1" style={{ color: INK }}>{w.marker}</p>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 p-7 sm:p-9" style={{ background: PAPER }}>
          <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>Your log for week {w.n}</p>
          <button onClick={() => onToggle(w.n, { done: !l.done })} className="flex items-center gap-2.5 mt-4" data-testid={`week-done-${w.n}`}>
            <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={l.done ? { background: DEEP } : { border: "1.5px solid rgba(22,40,29,0.2)", background: "#fff" }}>
              {l.done && <Check size={13} className="text-white" />}
            </span>
            <span className="text-sm font-semibold" style={{ color: INK }}>Week completed</span>
          </button>
          <div className="mt-5 space-y-3">
            <div>
              <p className="text-[11px] mb-1" style={{ color: "rgba(22,40,29,0.5)" }}>Body weight, kg</p>
              <input value={l.weight || ""} onChange={(e) => onToggle(w.n, { weight: e.target.value })} type="number" className="w-28 rounded-xl px-3 py-2 text-sm" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.12)" }} data-testid={`week-weight-${w.n}`} />
            </div>
            <div>
              <p className="text-[11px] mb-1" style={{ color: "rgba(22,40,29,0.5)" }}>One line about the week</p>
              <textarea value={l.note || ""} onChange={(e) => onToggle(w.n, { note: e.target.value })} rows={3} className="w-full rounded-xl px-3 py-2 text-sm" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.12)" }} data-testid={`week-note-${w.n}`} />
            </div>
          </div>
          <p className="text-[10px] mt-4" style={{ color: "rgba(22,40,29,0.4)" }}>Photographs on this timeline arrive with the tracking release.</p>
        </div>
      </div>
    </motion.div>
  );
}

function Plates() {
  const [k, setK] = useState(PLATES[0].key);
  const p = PLATES.find((x) => x.key === k);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-5">
        {PLATES.map((x) => (
          <button key={x.key} onClick={() => setK(x.key)} className="rounded-full px-4 py-2 text-sm font-medium transition-colors"
            style={k === x.key ? { background: DEEP, color: "#fff" } : { background: "#fff", color: INK, border: "1px solid rgba(22,40,29,0.12)" }} data-testid={`plate-${x.key}`}>{x.name}</button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <p className="text-sm italic mb-4" style={{ color: "rgba(22,40,29,0.55)" }}>{p.line}</p>
          <div className="space-y-2">
            {p.meals.map((m) => (
              <div key={m.m} className="rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-5" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.1)" }}>
                <p className="text-[10px] tracking-[0.18em] uppercase w-24 shrink-0" style={{ color: DEEP }}>{m.m}</p>
                <p className="text-sm" style={{ color: INK }}>{m.d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function Vitality() {
  const [data, patch] = useProgress("vitality", { intake: null, log: {} });
  const [open, setOpen] = useState(false);
  const [week, setWeek] = useState(null);
  const [entrance, setEntrance] = useState(false);
  useEffect(() => { setEntrance(true); }, []);
  useEffect(() => {
    const id = requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    return () => cancelAnimationFrame(id);
  }, [entrance, !!data.intake]);
  const program = useMemo(() => (data.intake ? buildProgram(data.intake) : null), [data.intake]);
  const macros = useMemo(() => (data.intake ? macroTargets(data.intake) : null), [data.intake]);
  const log = data.log || {};
  const doneWeeks = Object.values(log).filter((l) => l.done).length;
  const toggleWeek = (n, p) => patch((d) => ({ log: { ...(d.log || {}), [n]: { ...((d.log || {})[n] || {}), ...p } } }));

  return (
    <main className="pt-16" style={{ background: PAPER }} data-testid="platform-page-vitality">
      {/* ============================================ hero band */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${FOREST} 0%, #17492F 55%, #0C2A1D 100%)` }} data-testid="vitality-hero">
        <div className="absolute -right-[10%] top-[-20%] w-[45vw] h-[45vw] rounded-full blur-[120px] opacity-30 drift" style={{ background: `radial-gradient(circle, ${LIME}, transparent 70%)` }} />
        <div className="absolute -left-[14%] bottom-[-16%] w-[40vw] h-[40vw] rounded-full blur-[130px] opacity-25 drift" style={{ background: "radial-gradient(circle, #6FD08C, transparent 70%)", animationDelay: "3s" }} />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-14 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <div className="lg:col-span-5">
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] tracking-[0.28em] uppercase font-semibold text-white/60">Fitness and Nutrition</motion.p>
              <h1 className="font-display text-[44px] sm:text-[60px] lg:text-[68px] mt-5 leading-[0.94] text-white">
                {[["Lift.", 0], ["Eat.", 1], ["\n", -1], ["Retest", 2], ["in", 3], ["\n", -1], ["twelve", 4], ["weeks.", 5]].map(([w, i], n) =>
                  w === "\n" ? <br key={n} /> : (
                    <motion.span key={n} initial={{ opacity: 0, y: 24, filter: "blur(8px)" }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      transition={{ delay: 0.15 + i * 0.09, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="inline-block mr-[0.22em]" style={{ color: i >= 4 ? LIME : "#fff" }}>{w}</motion.span>
                  ))}
              </h1>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="text-white/65 text-[16px] sm:text-[18px] mt-6 max-w-sm leading-relaxed">
                Training and food, written as one plan around the body and the kit you actually have.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="mt-8">
                {!program || entrance ? (
                  <div className="flex flex-wrap items-center gap-4">
                    <button onClick={() => (program ? setEntrance(false) : setOpen(true))} className="group inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-semibold transition-transform hover:-translate-y-0.5"
                      style={{ background: LIME, color: FOREST, boxShadow: `0 24px 50px -20px ${LIME}` }} data-testid={program ? "vitality-back-to-programme" : "vitality-begin"}>
                      {program ? "Back to my programme" : "Answer the three"} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
                    </button>
                    <p className="text-[13px] text-white/50">Ninety seconds.<br className="hidden sm:block" /> No account, no photographs.</p>
                  </div>
                ) : (
                  <div className="rounded-[1.6rem] p-6 sheen max-w-sm" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.14)", backdropFilter: "blur(16px)" }}>
                    <div className="flex items-baseline justify-between">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-white/45">Your programme</p>
                      <p className="text-[11px]" style={{ color: LIME }}>{doneWeeks} of 12 logged</p>
                    </div>
                    <p className="font-display text-xl text-white mt-2 leading-snug">{program.expectancy.headline}</p>
                    <div className="mt-4 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.14)" }}>
                      <motion.div className="h-full rounded-full" style={{ background: LIME }} initial={{ width: 0 }} animate={{ width: `${(doneWeeks / 12) * 100}%` }} transition={{ duration: 0.8 }} />
                    </div>
                    <div className="mt-4 flex gap-2">
                      <a href="#arc" className="flex-1 text-center rounded-full px-4 py-2.5 text-sm font-semibold" style={{ background: LIME, color: FOREST }} data-testid="vitality-open-arc">Open the arc</a>
                      <button onClick={() => setOpen(true)} className="rounded-full px-4 py-2.5 text-white/70" style={{ border: "1px solid rgba(255,255,255,0.22)" }} data-testid="vitality-redo"><RotateCcw size={14} /></button>
                    </div>
                    <button onClick={() => { setEntrance(true); setWeek(null); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="mt-4 text-[12px] inline-flex items-center gap-1.5 text-white/45 hover:text-white/70 transition-colors" data-testid="vitality-to-entrance">
                      <ArrowLeft size={11} /> Back to the entrance
                    </button>
                  </div>
                )}
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-7" data-testid="vitality-hero-animation">
              <FitnessHero accent={LIME} />
            </motion.div>
          </div>

          {program && !entrance && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-10" data-testid="vitality-stats">
              {[
                ["The aim", GOAL_LABEL[program.goal] || "Consistency"],
                ["Sessions a week", `${program.days} × ${program.sessions[0].minutes} min`],
                ["What you have", TIER_LABEL[program.tier]],
                ["Protein a day", macros.known ? `${macros.protein} g` : macros.protein],
              ].map(([k, v], i) => (
                <motion.div key={k} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.07 }}
                  className="rounded-2xl px-5 py-4" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <p className="text-[9px] tracking-[0.18em] uppercase text-white/40">{k}</p>
                  <p className="font-display text-lg text-white mt-1 leading-tight">{v}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================================ the arc */}
      <section id="arc" className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        {program && !entrance ? (
          <>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <div>
                <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>The reconstruction</p>
                <h2 className="font-display text-3xl sm:text-4xl mt-2" style={{ color: INK }}>Twelve weeks as one line</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {program.phases.map((p) => (
                  <span key={p.n} className="rounded-full px-3 py-1.5 text-[11px] font-semibold" style={{ background: "rgba(76,175,125,0.14)", color: DEEP }}>{p.n}. {p.name}</span>
                ))}
              </div>
            </div>
            <Arc program={program} log={log} open={week} setOpen={setWeek} />
            <AnimatePresence>
              {week ? <WeekCard key={week} w={program.weeks.find((x) => x.n === week)} program={program} l={log[week] || {}} onToggle={toggleWeek} />
                : <p key="hint" className="text-sm text-center" style={{ color: "rgba(22,40,29,0.5)" }}>Tap any week on the line to open it.</p>}
            </AnimatePresence>

            <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-5">
              <div className="lg:col-span-7 rounded-[1.8rem] p-7 sm:p-9 relative overflow-hidden grain text-white" style={{ background: `linear-gradient(150deg, ${FOREST}, #1C5637)` }}>
                <p className="text-[10px] tracking-[0.24em] uppercase text-white/50">What to expect, honestly</p>
                <h3 className="font-display text-2xl sm:text-3xl mt-2 leading-tight">{program.expectancy.headline}</h3>
                <p className="text-white/75 mt-4">{program.expectancy.detail}</p>
                <p className="text-sm mt-4" style={{ color: LIME }}>{program.expectancy.honest}</p>
              </div>
              <div className="lg:col-span-5 rounded-[1.8rem] p-7" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.1)" }}>
                <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>Your daily targets</p>
                <div className="mt-4 space-y-2.5">
                  {(macros.known
                    ? [["Energy", `${macros.kcal} kcal`], ["Protein", `${macros.protein} g`], ["Fat", `${macros.fat} g`], ["Carbohydrate", `${macros.carbs} g`]]
                    : [["Energy", macros.kcal], ["Protein", macros.protein], ["Fat", macros.fat], ["Carbohydrate", macros.carbs]]
                  ).map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between border-b pb-2" style={{ borderColor: "rgba(22,40,29,0.1)" }}>
                      <span className="text-sm" style={{ color: "rgba(22,40,29,0.6)" }}>{k}</span>
                      <span className="font-display text-lg" style={{ color: INK }}>{v}</span>
                    </div>
                  ))}
                </div>
                {!macros.known && <p className="text-[11px] mt-3" style={{ color: "rgba(22,40,29,0.45)" }}>Add your body weight in the three questions and these become numbers.</p>}
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-[2rem] p-8 sm:p-12 relative overflow-hidden" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.1)" }}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5">
                <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>The arc</p>
                <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight" style={{ color: INK }}>Four phases, one line that climbs</h2>
                <p className="text-sm mt-4" style={{ color: "rgba(22,40,29,0.6)" }}>It appears here the moment the three questions are answered, with your sessions written into every week of it.</p>
                <button onClick={() => setOpen(true)} className="mt-7 inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{ background: DEEP, boxShadow: "0 18px 40px -18px rgba(30,107,69,0.8)" }} data-testid="vitality-begin-2">
                  Answer the three <ArrowRight size={15} />
                </button>
              </div>
              <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {[["Foundation", "Weeks 1–3", "Learn it light"], ["Load", "Weeks 4–6", "Add every week"], ["Build", "Weeks 7–9", "The hard block"], ["Reconstruction", "Weeks 10–12", "Retest week one"]].map(([p, w, d], i) => (
                  <motion.div key={p} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="rounded-2xl p-4" style={{ background: `rgba(76,175,125,${0.08 + i * 0.06})`, marginTop: i * 10 }}>
                    <p className="font-display text-3xl leading-none" style={{ color: DEEP, opacity: 0.35 }}>{i + 1}</p>
                    <p className="font-display text-base mt-2" style={{ color: INK }}>{p}</p>
                    <p className="text-[10px] tracking-[0.14em] uppercase mt-1" style={{ color: DEEP }}>{w}</p>
                    <p className="text-[12px] mt-1.5" style={{ color: "rgba(22,40,29,0.55)" }}>{d}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ============================================ films band */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(180deg, #0C2A1D, #123C28)` }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grain">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">01 · in build</p>
              <h2 className="font-display text-3xl sm:text-4xl text-white mt-1.5">{SECONDARY[0].name}</h2>
              <p className="text-sm text-white/50 mt-2 max-w-md">{SECONDARY[0].line}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(168,224,106,0.16)", color: LIME }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: LIME }} />{SECONDARY[0].state}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {MOVEMENTS.slice(0, 6).map((m, i) => (
              <div key={m.id} className="rounded-2xl overflow-hidden group" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }} data-testid={`film-${m.id}`}>
                <div className="aspect-[4/5] relative flex items-center justify-center" style={{ background: `linear-gradient(150deg, rgba(168,224,106,${0.1 + (i % 3) * 0.05}), rgba(15,51,36,0.4))` }}>
                  <span className="w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "rgba(255,255,255,0.9)", color: FOREST }}><Play size={14} /></span>
                  <span className="absolute bottom-2 right-2.5 text-[9px] tracking-wide uppercase text-white/40">soon</span>
                </div>
                <p className="text-[12px] font-medium px-3 py-2.5 text-white/80">{m.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ nutrition + rooms */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 rounded-[1.8rem] p-7 sm:p-8" style={{ background: "#F8FBF6", border: "1px solid rgba(22,40,29,0.1)" }}>
            <div className="flex items-start gap-3.5 mb-6">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(76,175,125,0.14)", color: DEEP }}><Utensils size={18} /></span>
              <div>
                <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: "rgba(22,40,29,0.45)" }}>02 · browsable now</p>
                <h3 className="font-display text-2xl" style={{ color: INK }}>{SECONDARY[3].name}</h3>
                <p className="text-sm mt-1" style={{ color: "rgba(22,40,29,0.55)" }}>{SECONDARY[3].line}</p>
              </div>
            </div>
            <Plates />
          </div>
          <div className="lg:col-span-5 space-y-4">
            {[SECONDARY[1], SECONDARY[4], SECONDARY[2]].map((s) => (
              <div key={s.key} className="rounded-[1.8rem] p-6" style={{ background: "rgba(76,175,125,0.08)", border: "1px solid rgba(76,175,125,0.24)" }} data-testid={`vsec-${s.key}`}>
                <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "#fff", color: DEEP }}>
                  {s.key === "session" ? <Play size={17} /> : s.key === "tracking" ? <LineChart size={17} /> : <Dumbbell size={17} />}
                </span>
                <h3 className="font-display text-xl mt-3.5" style={{ color: INK }}>{s.name}</h3>
                <p className="text-sm mt-1.5" style={{ color: "rgba(22,40,29,0.55)" }}>{s.line}</p>
                {s.key === "tracking" && (
                  <div className="mt-4 flex items-end gap-1 h-14">
                    {LIFT.map((h, j) => (
                      <motion.span key={j} initial={{ height: 0 }} whileInView={{ height: `${100 - h * 0.9}%` }} viewport={{ once: true }} transition={{ delay: j * 0.04 }}
                        className="flex-1 rounded-t" style={{ background: log[j + 1]?.done ? DEEP : "rgba(30,107,69,0.2)" }} />
                    ))}
                  </div>
                )}
                <p className="text-[11px] mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1" style={{ background: "#fff", color: DEEP }}>
                  <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: GREEN }} />{s.state}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ borderTop: "1px solid rgba(22,40,29,0.1)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
          <p className="kicker mb-4" style={{ color: "rgba(22,40,29,0.45)" }}>The other doors</p>
          <div className="flex flex-wrap gap-2.5">
            {PLATFORMS.filter((x) => x.key !== "vitality").map((x) => (
              <Link key={x.key} to={x.path} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-transform hover:-translate-y-0.5" style={{ background: "#fff", border: "1px solid rgba(22,40,29,0.1)", color: INK }} data-testid={`door-${x.key}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: x.hue }} />{x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {open && <VitalityIntake initial={data.intake} onDone={(v) => { patch({ intake: v }); setOpen(false); setWeek(1); setEntrance(false); }} onCancel={() => setOpen(false)} />}
      </AnimatePresence>
    </main>
  );
}
